import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";

export default function AboutAndServe() {
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [deals, setDeals] = useState([]);
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(0);

  const categories = ["pizza", "burger", "extras", "drink"];

  const categoryMap = {
    pizza: "Pizzas",
    burger: "Burgers",
    drink: "Drinks",
    extras: "Extras",
  };

  const testimonials = [
    {
      name: "Ali Khan",
      text: "Best pizza I’ve ever had! The crust is perfect and the toppings are fresh.",
    },
    {
      name: "Sara Ahmed",
      text: "Fast delivery and amazing flavors. I’m ordering again tonight!",
    },
    {
      name: "Hamza Raza",
      text: "Love the attention to detail. Every pizza feels crafted with care.",
    },
  ];

  const fadeUp = {
    hidden: { opacity: 0, y: 60 },
    show: { opacity: 1, y: 0, transition: { duration: 0.8 } },
  };

  // Fetch products
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await fetch(
          "https://supizza-backend.onrender.com/api/products",
        );
        const data = await res.json();

        const selected = categories.map((cat) => {
          const filtered = data.filter(
            (item) => item.category?.toLowerCase() === cat,
          );
          if (!filtered.length) return null;
          return filtered[Math.floor(Math.random() * filtered.length)];
        });

        setItems(selected.filter(Boolean));

        const dealItems = data
          .filter((item) => item.category?.toLowerCase() === "deal")
          .slice(0, 3);

        setDeals(dealItems);
      } catch (err) {
        console.error(err);
      }
    };

    fetchProducts();
  }, []);

  // Testimonials auto slide
  useEffect(() => {
    const interval = setInterval(() => {
      setDirection(1);
      setCurrent((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  const paginate = (index) => {
    setDirection(index > current ? 1 : -1);
    setCurrent(index);
  };

  return (
    <div className="bg-white text-gray-900 overflow-hidden">
      {/* ================== */}
      {/* ABOUT US */}
      {/* ================== */}
      <section className="max-w-7xl mx-auto px-6 py-32 grid md:grid-cols-12 gap-12 items-center">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="md:col-span-6 space-y-8"
        >
          <p className="text-xs tracking-[0.4em] text-red-700 uppercase">
            About Us
          </p>

          <h2 className="text-5xl font-light leading-tight">
            Pizza built with
            <span className="block text-red-700 mt-2">intention & flavor.</span>
          </h2>

          <p className="text-gray-600 leading-relaxed max-w-md">
            We treat pizza like design — carefully balanced, thoughtfully
            layered, and built for consistency.
          </p>

          <div className="h-[2px] w-20 bg-red-700"></div>
        </motion.div>

        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="md:col-span-6"
        >
          <motion.img
            whileHover={{ scale: 1.04 }}
            transition={{ duration: 0.6 }}
            src="https://images.unsplash.com/photo-1604382355076-af4b0eb60143"
            alt="Pizza"
            className="rounded-3xl w-full h-[520px] object-cover shadow-xl"
          />
        </motion.div>
      </section>

      {/* ================== */}
      {/* WHAT WE SERVE */}
      {/* ================== */}
      <section className="bg-gray-50 py-28 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <p className="text-xs tracking-[0.4em] uppercase text-red-700 mb-6">
              What We Serve
            </p>
            <h2 className="text-4xl md:text-5xl font-light">
              Crafted across categories.
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-10">
            {items.map((item) => (
              <motion.div
                key={item._id}
                whileHover={{ scale: 1.05 }}
                className="group cursor-pointer"
                onClick={() =>
                  navigate("/menu", { state: { category: item.category } })
                }
              >
                <div className="overflow-hidden rounded-2xl mb-6 bg-gray-100">
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-full h-64 object-contain"
                  />
                </div>

                <p className="text-sm text-gray-500 capitalize mb-3">
                  {categoryMap[item.category.toLowerCase()] || item.category}
                </p>

                <div className="h-[2px] w-12 bg-red-700 group-hover:w-24 transition-all duration-300"></div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ================== */}
      {/* CTA */}
      {/* ================== */}
      <section className="py-24 text-center bg-red-700 text-white">
        <motion.h3
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="text-3xl font-light mb-6"
        >
          Your Pizza Awaits
        </motion.h3>

        <motion.button
          whileHover={{ scale: 1.05 }}
          className="border border-white px-10 py-3 hover:bg-white hover:text-red-700 transition"
          onClick={() => navigate("/menu")}
        >
          View Menu
        </motion.button>
      </section>

      {/* ================== */}
      {/* DEALS */}
      {/* ================== */}
      {deals.length > 0 && (
        <section className="py-32 px-6 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-20">
              <p className="text-xs tracking-[0.4em] uppercase text-red-700 mb-6">
                Exclusive Deals
              </p>
              <h2 className="text-5xl font-light">Curated for you.</h2>
            </div>

            <div className="grid md:grid-cols-3 gap-10">
              {deals.map((deal) => (
                <motion.div
                  key={deal._id}
                  whileHover={{ y: -8 }}
                  transition={{ duration: 0.3 }}
                  onClick={() =>
                    navigate("/menu", { state: { category: deal.category } })
                  }
                  className="bg-white rounded-3xl shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer overflow-hidden group"
                >
                  {/* Image */}
                  <div className="relative bg-gray-50">
                    <img
                      src={deal.image}
                      alt={deal.name}
                      className="w-full h-[300px] object-contain p-6"
                    />

                    {/* Badge */}
                    <span className="absolute top-6 left-6 bg-red-700 text-white text-xs px-4 py-1 rounded-full tracking-wider">
                      DEAL
                    </span>
                  </div>

                  {/* Info */}
                  <div className="p-8 space-y-3">
                    <h3 className="text-2xl font-light group-hover:text-red-700 transition">
                      {deal.name}
                    </h3>

                    <p className="text-gray-500 text-sm">
                      Limited time special offer
                    </p>

                    <div className="flex justify-between items-center pt-4">
                      <span className="text-lg font-medium text-gray-900">
                        Rs {deal.price}
                      </span>

                      <span className="text-sm text-red-700 group-hover:translate-x-2 transition-transform duration-300">
                        View →
                      </span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ================== */}
      {/* TESTIMONIALS */}
      {/* ================== */}
      <section className="py-32 bg-gray-50 text-center">
        <div className="max-w-4xl mx-auto px-6">
          <p className="text-xs tracking-[0.4em] uppercase text-red-700 mb-8">
            Testimonials
          </p>

          <AnimatePresence mode="wait">
            <motion.div
              key={current}
              initial={{ opacity: 0, x: direction > 0 ? 100 : -100 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: direction < 0 ? 100 : -100 }}
              transition={{ duration: 0.6 }}
            >
              <p className="text-3xl font-light mb-6 text-gray-800">
                “{testimonials[current].text}”
              </p>
              <p className="text-red-700 tracking-widest uppercase text-sm">
                {testimonials[current].name}
              </p>
            </motion.div>
          </AnimatePresence>

          <div className="flex justify-center mt-10 gap-3">
            {testimonials.map((_, i) => (
              <button
                key={i}
                onClick={() => paginate(i)}
                className={`h-2 w-2 rounded-full ${
                  current === i ? "bg-red-700" : "bg-gray-300"
                }`}
              />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
